import { Stack, Box, Button, Paper, Typography, Container } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, brand } from '../components/themes/main_theme.js';
import Header from '../components/menu/Header.tsx';
import { usePageContent } from '../data/cms.ts';
import MailIcon from '@mui/icons-material/Mail';
import FacebookIcon from '@mui/icons-material/Facebook';

const FACEBOOK_URL = 'https://www.facebook.com/share/g/wk5dhjKGcjzCTcL4/?mibextid=K35XfP';
const EMAIL = 'saltandlightministriesguate@gmail.com';

type ContactCardProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: string;
  onClick: () => void;
};

const ContactCard = ({ icon, title, description, action, onClick }: ContactCardProps) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 3, md: 4 },
      flex: 1,
      border: `1px solid ${brand.hairline}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'rgba(165,92,8,0.10)',
        color: 'secondary.main',
        mb: 2,
      }}
    >
      {icon}
    </Box>
    <Typography variant="h5" color="primary" sx={{ mb: 1 }}>{title}</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>{description}</Typography>
    {/* Previously color="info", which is undefined in this palette and so fell
        back to MUI's default blue — off-brand on every contact button. */}
    <Button variant="contained" color="primary" onClick={onClick} sx={{ mt: 'auto' }}>
      {action}
    </Button>
  </Paper>
);

export const ContactButtons = () => (
  <Container maxWidth="md" sx={{ mb: 6 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="stretch">
      <ContactCard
        icon={<FacebookIcon sx={{ fontSize: 28 }} />}
        title="Facebook"
        description="Message us for updates, questions, or to connect with our ministry."
        action="Open Facebook"
        onClick={() => window.open(FACEBOOK_URL, '_blank', 'noopener,noreferrer')}
      />
      <ContactCard
        icon={<MailIcon sx={{ fontSize: 28 }} />}
        title="Email"
        description="Write to us directly for personal inquiries, prayer requests, or partnerships."
        action="Send an email"
        onClick={() => window.open(`mailto:${EMAIL}`)}
      />
    </Stack>
  </Container>
);

const ContactUs = () => {
  const content = usePageContent('CONTACT');
  const text = (key: string, fallback: string) => content[key] || fallback;

  return (
  <ThemeProvider theme={lightTheme}>
    <Header name={'CONTACT US'} />
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 } }}>
      <Typography variant="h2" color="primary" sx={{ mb: 1 }}>
        {text('heading', "We'd love to hear from you")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 5 }}>
        {text('intro', "Whether you're planning a team, praying for the work, or simply want to say hello.")}
      </Typography>
    </Container>
    <ContactButtons />
  </ThemeProvider>
  );
};

export default ContactUs;
